import {
    Component,
    DestroyRef,
    input,
    OnInit,
    signal,
    ViewEncapsulation,
} from '@angular/core';
import { Button } from '../button/button.component';
import { NgClass, NgFor } from '@angular/common';
import { ToastMessage, ToastService } from './toast.service';
import { timer, Subscription, filter } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Icon } from '../icon/icon.component';
import { Router, NavigationEnd } from '@angular/router';

/**
 * Container de notificações (toasts).
 *
 * Observa o {@link ToastService} e exibe cada mensagem recebida, removendo-a
 * automaticamente após sua duração (ou {@link timeout} padrão) ou ao ser fechada
 * pelo usuário. O ícone e a cor variam conforme a severidade da mensagem.
 */
@Component({
    selector: 'ai-toast',
    standalone: true,
    imports: [Button, Icon, NgClass, NgFor],
    templateUrl: './toast.component.html',
    styleUrls: ['./toast.component.scss'],
    encapsulation: ViewEncapsulation.Emulated,
})
export class Toast implements OnInit {
    protected readonly messages = signal<ToastMessage[]>([]);

    /** Duração padrão (ms) de exibição quando a mensagem não define a sua. */
    protected readonly timeout = input<number>(5000);
    private routerSubscription!: Subscription;

    constructor(
        private readonly toastService: ToastService,
        private readonly router: Router,
        private readonly destroyRef: DestroyRef,
    ) {}

    ngOnInit(): void {
        this.toastService
            .onMessageAdded()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (message: ToastMessage | null) => {
                    if (message) {
                        this.messages.update((old) => [...old, message]);

                        timer(message.duration ?? this.timeout()).subscribe(
                            () => {
                                const index = this.messages().findIndex(
                                    (m) => m.id === message.id,
                                );

                                if (index !== -1) {
                                    this.messages.set([
                                        ...this.messages().slice(0, index),
                                        ...this.messages().slice(index + 1),
                                    ]);
                                }
                                this.toastService.removeMessage();
                            },
                        );
                    }
                },
            });
    }

    protected getMessageLevelClass(message: ToastMessage): string {
        return `toast-message ${message.severity} ai-is-visible`;
    }

    protected getSeverityIcon(message: ToastMessage): string {
        switch (message.severity) {
            case 'success': return 'fa-solid fa-circle-check';
            case 'info':    return 'fa-solid fa-circle-info';
            case 'alert':   return 'fa-solid fa-triangle-exclamation';
            case 'error':   return 'fa-solid fa-circle-xmark';
            default:        return 'fa-solid fa-circle-info';
        }
    }

    protected close(message: ToastMessage): void {
        const index = this.messages().findIndex((m) => m.id === message.id);

        if (index !== -1) {
            this.messages.set([
                ...this.messages().slice(0, index),
                ...this.messages().slice(index + 1),
            ]);
        }

        this.toastService.removeMessage();
    }
}
